import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk'
import { LD_FLAG_PRODUCT_ENROLLME_DOCUMENT_CENTER } from './constants'

type DisplayCarrierNotice = {
  [name: string]: {
    State: string[]
    Website: string
  }
}

type BridgeToEmployeeExperienceShopForHealth = {
  clientPublicKey: {
    [key: string]: boolean
  }
  employeePublicKey: {
    [key: string]: boolean
  }
}

export type TopBannerAlertFlag = {
  enabled: boolean
  type: 'info' | 'warn'
  message: string
  ctaLabel?: string
  ctaUrl?: string
}

/** JSON object variation for `product.enrollme.document-center` in LaunchDarkly. */
export type ProductEnrollmeDocumentCenterConfig = {
  enabledForAll?: boolean
  enabledClients?: string[]
}

/** All supported flag shapes: booleans, object config, or legacy JSON array allowlist. */
export type ProductEnrollmeDocumentCenterFlag =
  | boolean
  | ProductEnrollmeDocumentCenterConfig
  | string[]

export type UseFeatureFlagsLoginParams = {
  /**
   * The identifying key that identifies a user in our systems, such as a user ID.
   */
  key: string
  /**
   * The user's primary email address.
   */
  email: string
  /**
   * The company id of the user's employer.
   */
  companyId: string
}

export type UseFeatureFlags = {
  /**
   * An object containing all feature flags used by the application.
   */
  flags: {
    /**
     * Controls visibility of the QLE v1 feature.
     */
    ['qle-v-1']: boolean
    /**
     * Controls visibility of the QLE v2 feature.
     */
    ['enrollme.qle-v-2']: boolean

    /**
     * Controls if impersonation is allowed for QLE V2 (only on dev).
     */
    ['enrollme.qle-v-2-allow-impersonation']: boolean
    /**
     * Controls visibility of the Zendesk widget.
     */
    ['customer-service.fe.zendesk-widget-enabled']: boolean
    /**
     * Controls visibility of Renewals feature
     */
    ['product.enrollme.renewals']: boolean
    /**
     * Controls visibility of custom split family feature
     */
    ['product.enrollme.custom.split.family']: boolean
    /**
     * Controls visibility of professional level inputs
     */
    ['product.enrollme.professional-experience-inputs']: boolean
    /**
     * Contains the time when the enrollment period ends. (tech debt)
     */
    ['product.enrollme.enrollment-cutoff-time']: string
    /**
     * Controls individual selection of members in QLE process
     */
    ['product.enrollme.individual-member-selection-qle']: boolean
    /**
     * Determines if terms and conditions check should occur on shop for health page during QLE process
     */
    ['product.enrollme.terms-and-condition-qle']: boolean
    /**
     * Determines if a notice should be shown for a specific Carrier in a given state
     */
    ['product.enrollme.shop-display-carrier-notice']: DisplayCarrierNotice | {}
    /**
     * When this flag is turned on we validate the length of the split family to allow QLE only if number of groups is <= 1
     */
    ['product.enrollme.check-split-family-length-qle']: boolean
    /**
     * When this flag is turned on the user is allowed to access the add a person QLE flow
     */
    ['product.enrollme.add-person-qle']: boolean
    /**
     * Controls if address verification feature is active
     */
    ['feature-address-verification']: boolean
    /**
     * Temp flag - Controls if additional medicare questions are active
     */
    ['temp.medicare.questions.ui']: boolean
    /**
     * Controls if QLE Relocation flow is available
     */
    ['qle.relocation-flow.ui']: boolean
    /**
     * Temp flag for controlling ICHRA contribution notice.
     * ICHRA contribution notice also requires company config "DisplayContributionsToEmployees" to be true
     */
    ['temp.ichra-contribution.ui']: boolean
    /**
     * Temp flag to control citizenship questions for employee form
     */
    ['temp.ui.citizenship-question']: boolean
    /**
     * Used to display the activity log popup reason.
     */
    ['enrollme.activity-log-reason-popup-enabled']: boolean
    /**
     * Feature flag to indicate provider search outage
     */
    ['enrollme.provider-search-outage']: boolean
    /**
     * Feature flag to indicate if the organ donor question is visible
     */
    ['enrollme.organ-donor-question']: string[]
    /**
     * Controls if user will be redirected to employee experience shop for health
     */
    ['enrollme.uses-bridge-to-employee-experience-shop-for-health']:
      | BridgeToEmployeeExperienceShopForHealth
      | {}
    /**
     * Temp flag - Shows "Select A Plan (NEW)" button for admins spoofing users whose employeePublicKey is in the flag object
     */
    ['temp.enrollme.epk-will-see-eeux-shop-health-plans-button']: Record<
      string,
      { email: string; name: string }
    >
    /**
     * Document Center: JSON object with `enabledForAll` / `enabledClients`, legacy `true`/`false`,
     * or legacy JSON array of client public keys (allowlist). Key: `LD_FLAG_PRODUCT_ENROLLME_DOCUMENT_CENTER`.
     */
    ['product.enrollme.document-center']: ProductEnrollmeDocumentCenterFlag
    /**
     * Controls the platform-wide top banner alert (notices, warnings, alerts).
     */
    ['platform.top-banner-alert']: TopBannerAlertFlag

    /**
     * Controls visibility of the system generated documents section
     */
    ['enrollme.system-file-generated-enabled']: boolean
  }
  /**
   * Callback methods used to perform actions on our feature flag storage, such as identifying a user after login.
   */
  methods: {
    /**
     * Identifies a user after a successful login.
     */
    identify: (params: UseFeatureFlagsLoginParams) => void
  }
}

export const useFeatureFlags = (): UseFeatureFlags => {
  try {
    const flags = useFlags()
    const ldClient = useLDClient()
    const identify = ({ key, email, companyId }: UseFeatureFlagsLoginParams) => {
      ldClient
        ?.identify({ key, email, custom: { companyId } })
        .catch((e) => console.error('LaunchDarkly - Error identifying user', e))
    }
    return {
      flags,
      methods: { identify },
    } as UseFeatureFlags
  } catch (e) {
    console.error('Error initializing LaunchDarkly', e)
    return defaultFeatureFlags
  }
}

const defaultFeatureFlags: UseFeatureFlags = {
  /**
   * An object containing all feature flags used by the application.
   */
  flags: {
    /**
     * Controls visibility of the QLE v1 feature.
     */
    'qle-v-1': false,
    /**
     * Controls visibility of the QLE v2 feature.
     */
    'enrollme.qle-v-2': false,
    /**
     * Controls if impersonation is allowed for QLE V2 (only on dev).
     */
    'enrollme.qle-v-2-allow-impersonation': false,
    /**
     * Controls visibility of the Zendesk widget.
     */
    'customer-service.fe.zendesk-widget-enabled': false,
    /**
     * Controls visibility of Renewals feature
     */
    'product.enrollme.renewals': false,
    /**
     * Controls visibility of custom split family feature
     */
    'product.enrollme.custom.split.family': false,
    /**
     * Controls visibility of professional level inputs
     */
    'product.enrollme.professional-experience-inputs': false,
    /**
     * Contains the time when the enrollment period ends. (tech debt)
     */
    'product.enrollme.enrollment-cutoff-time': '17:00:02',
    /**
     * Controls individual selection of members in QLE process
     */
    'product.enrollme.individual-member-selection-qle': false,
    /**
     * Determines if terms and conditions check should occur on shop for health page during QLE process
     */
    'product.enrollme.terms-and-condition-qle': false,
    /**
     * Determines if a notice should be shown for a specific Carrier in a given state
     */
    'product.enrollme.shop-display-carrier-notice': {},
    /**
     * When this flag is turned on we validate the length of the split family to allow QLE only if number of groups is <= 1
     */
    'product.enrollme.check-split-family-length-qle': false,
    /**
     * When this flag is turned on the user is allowed to access the add a person QLE flow
     */
    'product.enrollme.add-person-qle': false,
    /**
     * Controls if address verification feature is active
     */
    'feature-address-verification': false,
    /**
     * Temp flag - Controls if additional medicare questions are active
     */
    'temp.medicare.questions.ui': false,
    /**
     * Controls if QLE Relocation flow is available
     */
    'qle.relocation-flow.ui': false,
    /**
     * Temp flag for controlling ICHRA contribution notice.
     * ICHRA contribution notice also requires company config "DisplayContributionsToEmployees" to be true
     */
    'temp.ichra-contribution.ui': false,
    /**
     * Temp flag to control citizenship questions for employee form
     */
    'temp.ui.citizenship-question': false,
    /**
     * Used to display the activity log popup reason.
     */
    'enrollme.activity-log-reason-popup-enabled': false,
    /**
     * Feature flag to indicate provider search outage
     */
    'enrollme.provider-search-outage': false,
    /**
     * Controls if bridge authentication to employee experience shop for health is enabled
     */
    'enrollme.uses-bridge-to-employee-experience-shop-for-health': {
      clientPublicKey: {},
      employeePublicKey: {},
    },
    /**
     * Feature flag to indicate if the organ donor question is visible
     */
    'enrollme.organ-donor-question': [],
    /**
     * Temp flag - Shows "Select A Plan (NEW)" button for admins spoofing users whose employeePublicKey is in the flag object
     */
    'temp.enrollme.epk-will-see-eeux-shop-health-plans-button': {},
    /**
     * Default: on for all when LaunchDarkly is unavailable.
     */
    'product.enrollme.document-center': { enabledForAll: true, enabledClients: [] },
    'platform.top-banner-alert': {
      enabled: false,
      type: 'info',
      message: '',
    },
    /**
     * Controls visibility of the system generated documents section
     */
    'enrollme.system-file-generated-enabled': false,
  },
  /**
   * Callback methods used to perform actions on our feature flag storage, such as identifying a user after login.
   */
  methods: {
    /**
     * Identifies a user after a successful login.
     */
    identify: (_params: never) => {
      console.warn('No feature flag provider found.')
    },
  },
}

function toDocumentCenterState(
  v: ProductEnrollmeDocumentCenterFlag | undefined
): { enabledForAll: boolean; enabledClients: string[] } {
  if (v === true) {
    return { enabledForAll: true, enabledClients: [] }
  }
  if (v === false) {
    return { enabledForAll: false, enabledClients: [] }
  }
  // Legacy LaunchDarkly JSON: bare array of client public keys (allowlist only).
  // Must run before the plain-object branch: `typeof [] === 'object'`.
  if (Array.isArray(v)) {
    return {
      enabledForAll: false,
      enabledClients: v.filter((x): x is string => typeof x === 'string'),
    }
  }
  if (v && typeof v === 'object') {
    return {
      enabledForAll: Boolean(v.enabledForAll),
      enabledClients: Array.isArray(v.enabledClients) ? v.enabledClients : [],
    }
  }
  return { enabledForAll: false, enabledClients: [] }
}

/**
 * Whether Document Center should be shown for the given client.
 * `enabledForAll` shows for everyone; otherwise the client's public key must be in `enabledClients`.
 * Legacy `true` / `false` booleans and legacy JSON array allowlists are still supported.
 */
export function isProductEnrollmeDocumentCenterEnabled(
  flags: UseFeatureFlags['flags'],
  clientPublicKey: string | null | undefined
): boolean {
  const { enabledForAll, enabledClients } = toDocumentCenterState(
    flags[LD_FLAG_PRODUCT_ENROLLME_DOCUMENT_CENTER] as ProductEnrollmeDocumentCenterFlag
  )
  return enabledForAll || (clientPublicKey != null && enabledClients.includes(clientPublicKey))
}

/**
 * When the flag is not `enabledForAll` and the allowlist is non-empty, we need the client id before
 * we can allow or deny access. Used by the route gate to avoid redirecting before session is ready.
 *
 * Also returns true while the flag key is missing from `flags` (e.g. first paint with `useFlags()`
 * as `{}` before LaunchDarkly hydrates). Otherwise `toDocumentCenterState(undefined)` looks like
 * "deny with empty allowlist" and the gate would redirect before the real value arrives.
 */
export function isProductEnrollmeDocumentCenterAccessPending(
  flags: UseFeatureFlags['flags'],
  clientPublicKey: string | null | undefined
): boolean {
  const raw = flags[LD_FLAG_PRODUCT_ENROLLME_DOCUMENT_CENTER] as
    | ProductEnrollmeDocumentCenterFlag
    | undefined
  if (raw === undefined) {
    return true
  }
  const { enabledForAll, enabledClients } = toDocumentCenterState(raw)
  if (enabledForAll) {
    return false
  }
  if (clientPublicKey != null) {
    return false
  }
  return enabledClients.length > 0
}

export default useFeatureFlags
